import React from 'react';
import { trackClick } from '../../utils/analytics';
import { openSource, type SourceTrack } from '../common/SourcePane';
import { type WnItem, type WnQuery, type WnSettings, type WnWorld, fmtDay, tint } from './types';

/**
 * "In the world · IP & licensing activity" (prototype section 2): category
 * tabs with counts, an industry filter and a date range, then the published
 * items grouped per category ("View all <category> →" opens that tab).
 *
 * "View source" opens the page in the in-app source pane (components/common/
 * SourcePane); a modified click (new tab / window) keeps the browser's own.
 */

interface Props {
  settings: WnSettings;
  world: WnWorld;
  query: WnQuery;
  loading: boolean;
  onTab: (key: string) => void;
  onIndustry: (v: string) => void;
  onDates: (from: string, to: string) => void;
  onViewAll: (key: string) => void;
  onSource: (item: WnItem) => void;
  onClear: () => void;
  /** The page's tracker for the source pane's events (What's New's own, silent in the admin preview). */
  track?: SourceTrack;
}

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
);
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" />
  </svg>
);

const ctrlCls = 'relative inline-flex items-center gap-2 h-10 px-3.5 rounded-full border border-primary-300 bg-white text-sm text-grayscale-1200';

const defaultTrack: SourceTrack = (action, props) => trackClick(action, props);

export const ItemCard: React.FC<{ item: WnItem; color: string; onSource: (i: WnItem) => void; track?: SourceTrack }> =
  ({ item, color, onSource, track = defaultTrack }) => (
  <article className="flex flex-col gap-2 rounded-xl border border-primary-200 bg-white px-[22px] py-5 shadow-sm min-w-0">
    <div className="flex justify-between items-center gap-2">
      <span className="text-xs font-semibold px-2.5 py-[3px] rounded-md" style={{ background: tint(color), color }}>
        {item.type_label}
      </span>
      <span className="flex items-center gap-2">
        {item.status && item.status !== 'published' && (
          <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{item.status}</span>
        )}
        <span className="text-[13px] text-grayscale-1000 whitespace-nowrap">{fmtDay(item.item_date)}</span>
      </span>
    </div>
    <h4 className="m-0 text-[17px] font-semibold leading-snug text-grayscale-1300 break-words">{item.title}</h4>
    {(item.org || item.country) && (
      <span className="text-[13px] text-grayscale-1000">{[item.org, item.country].filter(Boolean).join(' · ')}</span>
    )}
    {item.summary && (
      <p className="m-0 text-sm leading-relaxed text-grayscale-1100 line-clamp-3">{item.summary}</p>
    )}
    <div className="flex justify-between items-center gap-3 mt-auto pt-1.5 flex-wrap">
      <div className="flex gap-1.5 flex-wrap">
        {item.amount && (
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-primary-100 text-grayscale-1300">{item.amount}</span>
        )}
        {item.industry && (
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary-100 text-grayscale-1200">{item.industry}</span>
        )}
      </div>
      {item.source_url && (
        <a href={item.source_url} target="_blank" rel="noopener noreferrer nofollow"
          onClick={e => {
            onSource(item);
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            openSource({ url: item.source_url!, title: item.title, surface: 'whatsnew', track,
                         props: { item_id: String(item.id), category: item.category, type_label: item.type_label } });
          }}
          className="text-sm font-medium text-primary-700 hover:text-primary-600 hover:underline underline-offset-2 whitespace-nowrap">
          View source →
        </a>
      )}
    </div>
  </article>
);

export const WorldNews: React.FC<Props> = ({ settings, world, query, loading, onTab, onIndustry, onDates, onViewAll,
                                            onSource, onClear, track }) => {
  const active = query.category || 'all';
  const tabs = [{ key: 'all', label: 'All' }, ...settings.categories.map(c => ({ key: c.key, label: c.label }))];
  const filtered = !!(query.industry || query.from || query.to);
  const visibleGroups = world.groups.filter(g => active !== 'all' || g.count > 0);
  const total = world.counts.all || 0;
  return (
    <section className="flex flex-col gap-5 border-t border-primary-200 pt-9" data-wn-section="world">
      <div className="flex items-center justify-between flex-wrap gap-3 pb-3.5 border-b border-primary-200">
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.08em] border border-primary-400 text-primary-700 px-3 py-[3px] rounded-full">
            In the world
          </span>
          <h2 className="m-0 text-[19px] font-semibold text-grayscale-1300">IP &amp; licensing activity</h2>
        </div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-3.5">
        <nav aria-label="News categories" className="flex flex-wrap gap-1 bg-primary-100 p-1 rounded-3xl max-w-full">
          {tabs.map(t => {
            const on = t.key === active;
            const n = world.counts[t.key] ?? 0;
            return (
              <button key={t.key} type="button" aria-pressed={on} onClick={() => onTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-colors ${on
                  ? 'bg-white text-grayscale-1300 font-semibold shadow-sm'
                  : 'text-grayscale-1100 hover:text-grayscale-1300'}`}>
                {t.label}
                <span className={`text-xs ${on ? 'text-primary-700' : 'text-grayscale-1000'}`}>{n}</span>
              </button>
            );
          })}
        </nav>
        <div className="flex gap-2.5 flex-wrap">
          <label className={`${ctrlCls} cursor-pointer`}>
            <span className="text-primary-700"><FilterIcon /></span>
            <span className="sr-only">Industry</span>
            <select value={query.industry} onChange={e => onIndustry(e.target.value)}
              className="border-none bg-transparent text-sm text-grayscale-1200 pr-1 focus:outline-none cursor-pointer max-w-[12rem]">
              <option value="">All industries</option>
              {settings.industries.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <div className={ctrlCls}>
            <span className="text-primary-700"><CalendarIcon /></span>
            <input type="date" aria-label="From date" value={query.from} max={query.to || undefined}
              onChange={e => onDates(e.target.value, query.to)}
              className="bg-transparent text-sm text-grayscale-1200 w-[8.4rem] focus:outline-none" />
            <span className="text-grayscale-1000">→</span>
            <input type="date" aria-label="To date" value={query.to} min={query.from || undefined}
              onChange={e => onDates(query.from, e.target.value)}
              className="bg-transparent text-sm text-grayscale-1200 w-[8.4rem] focus:outline-none" />
            {(query.from || query.to) && (
              <button type="button" onClick={() => onDates('', '')} aria-label="Clear dates"
                className="text-grayscale-1000 hover:text-grayscale-1300 px-0.5">×</button>
            )}
          </div>
        </div>
      </div>

      <div className={`flex flex-col gap-8 transition-opacity ${loading ? 'opacity-60' : ''}`}>
        {(total === 0 || visibleGroups.every(g => g.count === 0)) ? (
          <div className="rounded-xl border border-dashed border-primary-300 bg-primary-100/30 px-5 py-8 text-sm text-grayscale-1000 text-center flex flex-col items-center gap-2">
            {filtered || active !== 'all' ? (
              <>
                <span>No updates match these filters.</span>
                <button type="button" onClick={onClear}
                  className="text-sm font-medium text-primary-700 hover:underline underline-offset-2">Clear filters</button>
              </>
            ) : (
              <span>No news yet — the content team is gathering the latest IP &amp; licensing activity.</span>
            )}
          </div>
        ) : visibleGroups.map(g => (
          <div key={g.key} className="flex flex-col gap-3.5 pt-2">
            <div className="flex justify-between items-baseline gap-3 flex-wrap">
              <h3 className="m-0 text-lg font-semibold text-grayscale-1300 flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: g.color }} aria-hidden="true" />
                {g.label}
                <span className="text-sm font-normal text-grayscale-1000">
                  {g.count} update{g.count === 1 ? '' : 's'}
                </span>
              </h3>
              {active === 'all' && g.count > 0 && (
                <button type="button" onClick={() => onViewAll(g.key)}
                  className="text-sm font-medium text-primary-700 hover:text-primary-600 hover:underline underline-offset-2">
                  View all {g.label.toLowerCase()} →
                </button>
              )}
            </div>
            {g.items.length === 0 ? (
              <div className="text-sm text-grayscale-1000">No {g.label.toLowerCase()} updates for these filters.</div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
                {g.items.map(it => <ItemCard key={it.id} item={it} color={g.color} onSource={onSource} track={track} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default WorldNews;
